import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { hämtaDemostatus } from '../api/client';
import { TEMA, useMobil } from '../theme';
import InstallningarFlikar from '../components/InstallningarFlikar';

interface Användare {
  id: string;
  namn: string;
  roll: string;
  aktiv: boolean;
  email?: string | null;
  senast_inloggad?: string | null;
  daglig_sammanfattning: boolean;
  eftermiddag_paminnelse: boolean;
  // Bara i demon: gäster har ett slutdatum (ÅÅÅÅ-MM-DD).
  gast?: boolean;
  giltig_till?: string | null;
  // Gästens riktiga namn. Fältet namn är i demon alltid säljaren gästen ser som.
  gast_namn?: string | null;
  // Moduler användaren har tillgång till. Sätts av admin.
  moduler?: string[];
}

const MODULVAL: [string, string][] = [['restaurang', 'Restaurang'], ['anbud', 'Anbud'], ['inkop', 'Inköp']];

// Namnet som visas i listan: gästens eget om det finns, annars e-posten.
function visningsnamn(a: Användare): string {
  if (!a.gast) return a.namn;
  return a.gast_namn || a.email || a.namn;
}

const GÄST_DAGAR = 14;

function datumOm(dagar: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dagar);
  const två = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${två(d.getMonth() + 1)}-${två(d.getDate())}`;
}

// "Gäller till 22 okt", eller "Gick ut 22 okt" när dagen har passerat.
function giltigText(datum?: string | null): { text: string; utgången: boolean } {
  if (!datum) return { text: 'Inget slutdatum', utgången: false };
  const utgången = datum < datumOm(0);
  const d = new Date(`${datum}T12:00:00`);
  // Webbläsaren skriver "9 okt." med punkt. Den tas bort, annars blir det två i slutet av en mening.
  const dag = d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }).replace(/\.$/, '');
  return { text: utgången ? `Gick ut ${dag}` : `Gäller till ${dag}`, utgången };
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', fontSize: '14px', fontFamily: "'Inter', sans-serif",
  borderRadius: '2px', border: `1px solid ${TEMA.border}`, background: TEMA.bg, color: TEMA.ink,
  boxSizing: 'border-box', outline: 'none', minWidth: 0,
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: TEMA.inkFaint,
  display: 'block', marginBottom: '6px',
};

const btnPrimärStyle: React.CSSProperties = {
  padding: '9px 18px', borderRadius: '2px', background: TEMA.accent, color: TEMA.onAccent,
  fontFamily: "'Inter', sans-serif", fontSize: '13px', fontWeight: 500,
  border: `1px solid ${TEMA.accent}`, cursor: 'pointer',
};

const btnGhostStyle: React.CSSProperties = {
  padding: '9px 18px', borderRadius: '2px', background: 'transparent', color: TEMA.inkSoft,
  fontFamily: "'Inter', sans-serif", fontSize: '13px', border: `1px solid ${TEMA.border}`, cursor: 'pointer',
};

const ROLLNAMN: Record<string, string> = { admin: 'Admin', säljare: 'Säljare' };

function senastText(tid?: string | null): string {
  if (!tid) return 'Aldrig inloggad';
  const d = new Date(tid);
  const klocka = d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  const idag = new Date();
  const igår = new Date(); igår.setDate(idag.getDate() - 1);
  if (d.toDateString() === idag.toDateString()) return `Inloggad i dag ${klocka}`;
  if (d.toDateString() === igår.toDateString()) return `Inloggad i går ${klocka}`;
  const dagar = Math.floor((idag.getTime() - d.getTime()) / 86400000);
  if (dagar < 30) return `Inloggad för ${dagar} dagar sedan`;
  return `Inloggad ${d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

function felText(err: any, reserv: string): string {
  return err?.response?.data?.detail || reserv;
}

// Profilbilden om den finns, annars första bokstaven.
function Avatar({ id, namn, bildUrl }: { id: string; namn: string; bildUrl: string }) {
  const [saknas, setSaknas] = useState(false);
  const stil: React.CSSProperties = {
    width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0, marginRight: '14px',
  };
  if (!saknas) {
    return <img src={bildUrl} alt="" onError={() => setSaknas(true)} style={{ ...stil, objectFit: 'cover' }} />;
  }
  return (
    <div key={id} style={{
      ...stil, background: 'rgba(224,168,62,0.12)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontFamily: "'IBM Plex Mono', monospace", fontSize: '13px',
      fontWeight: 500, color: TEMA.accent,
    }}>
      {namn?.charAt(0).toUpperCase()}
    </div>
  );
}

export default function Admin() {
  const { profil, supabase, användare: inloggad } = useAuth();
  const jagId = inloggad?.id;
  const ärAdmin = profil?.roll === 'admin';

  const [lista, setLista] = useState<Användare[]>([]);
  const [laddar, setLaddar] = useState(true);
  const [fel, setFel] = useState<string | null>(null);

  const [visaForm, setVisaForm] = useState(false);
  const [nyEmail, setNyEmail] = useState('');
  const [nyNamn, setNyNamn] = useState('');
  const [nyRoll, setNyRoll] = useState('säljare');
  const [nyttLösenord, setNyttLösenord] = useState('');
  const [nyGiltigTill, setNyGiltigTill] = useState(datumOm(GÄST_DAGAR));
  const [demo, setDemo] = useState(false);
  // Texten med inloggningsuppgifter som admin skickar till gästen. Demon skickar inga mejl.
  const [inbjudan, setInbjudan] = useState<string | null>(null);
  const [kopierad, setKopierad] = useState(false);
  const [skapar, setSkapar] = useState(false);
  const [meddelande, setMeddelande] = useState('');

  const [öppen, setÖppen] = useState<string | null>(null);

  const ärMobil = useMobil();
  const [utkast, setUtkast] = useState<Användare | null>(null);
  const [arbetar, setArbetar] = useState(false);
  const [radBesked, setRadBesked] = useState<{ text: string; fel: boolean } | null>(null);

  const ladda = useCallback(async () => {
    try {
      const res = await api.get<Användare[]>('/auth/anvandare');
      setLista(res.data);
      setFel(null);
    } catch (err) {
      console.error('Kunde inte ladda användarna:', err);
      setFel('Kunde inte ladda användarna just nu.');
    } finally {
      setLaddar(false);
    }
  }, []);

  useEffect(() => {
    ladda();
  }, [ladda]);

  useEffect(() => {
    let aktuell = true;
    hämtaDemostatus().then(res => { if (aktuell) setDemo(!!res.data.demo); }).catch(() => {});
    return () => { aktuell = false; };
  }, []);

  function bildUrl(id: string): string {
    return supabase.storage.from('profilbilder').getPublicUrl(`${id}/avatar.jpg`).data.publicUrl;
  }

  function växlaÖppen(a: Användare) {
    setRadBesked(null);
    if (öppen === a.id) {
      setÖppen(null);
      setUtkast(null);
    } else {
      setÖppen(a.id);
      setUtkast({ ...a });
    }
  }

  async function skapaAnvändare(e: React.FormEvent) {
    e.preventDefault();
    setSkapar(true);
    setMeddelande('');
    try {
      const res = await api.post('/auth/skapa-anvandare', {
        email: nyEmail, password: nyttLösenord, namn: nyNamn, roll: nyRoll,
        ...(demo ? { giltig_till: nyGiltigTill } : {}),
      });
      if (res.data.status === 'ok') {
        if (demo) {
          const sista = res.data.giltig_till
            ? new Date(`${res.data.giltig_till}T12:00:00`).toLocaleDateString('sv-SE', { day: 'numeric', month: 'long' })
            : null;
          setInbjudan([
            `Hej ${nyNamn.trim()},`,
            '',
            'Här är din inloggning till demon av Vinsales.',
            '',
            `Adress: ${window.location.origin}`,
            `E-post: ${nyEmail.trim()}`,
            `Lösenord: ${nyttLösenord}`,
            '',
            sista ? `Inloggningen gäller till och med ${sista}.` : '',
            '',
            'Demon visar ett påhittat bolag, Fyrskepp Vinimport. Du är inloggad som säljaren Elsa och ser hennes kunder, möten och påminnelser. Klicka runt och ändra fritt, allt återställs varje natt. Skriv inte in riktiga uppgifter.',
          ].join('\n'));
          setKopierad(false);
        }
        setMeddelande(demo
          ? `${nyNamn.trim()} är tillagd som gäst. ${giltigText(res.data.giltig_till).text}. Demon skickar inga mejl, så skicka uppgifterna själv.`
          : `${nyNamn} är tillagd.`);
        setVisaForm(false);
        setNyEmail(''); setNyNamn(''); setNyttLösenord(''); setNyGiltigTill(datumOm(GÄST_DAGAR));
        ladda();
      } else {
        setMeddelande('Något gick fel: ' + res.data.meddelande);
      }
    } catch (err) {
      setMeddelande(felText(err, 'Något gick fel.'));
    } finally {
      setSkapar(false);
    }
  }

  async function spara(original: Användare) {
    if (!utkast) return;
    const ändrat: Record<string, unknown> = {};
    (['namn', 'roll', 'email', 'daglig_sammanfattning', 'eftermiddag_paminnelse'] as const).forEach(f => {
      if (utkast[f] !== original[f]) ändrat[f] = utkast[f];
    });
    if (original.gast && (utkast.gast_namn || '') !== (original.gast_namn || '')) {
      ändrat.gast_namn = utkast.gast_namn || '';
    }
    if (original.gast && (utkast.giltig_till || '') !== (original.giltig_till || '')) {
      ändrat.giltig_till = utkast.giltig_till || '';
    }
    if ((utkast.moduler || []).join() !== (original.moduler || []).join()) {
      if (!utkast.moduler?.length) {
        setRadBesked({ text: 'Välj minst en modul.', fel: true });
        return;
      }
      ändrat.moduler = utkast.moduler;
    }
    if (Object.keys(ändrat).length === 0) {
      setRadBesked({ text: 'Inget att spara.', fel: false });
      return;
    }
    setArbetar(true);
    try {
      await api.patch(`/auth/anvandare/${original.id}`, ändrat);
      setRadBesked({ text: 'Sparat.', fel: false });
      await ladda();
    } catch (err) {
      setRadBesked({ text: felText(err, 'Kunde inte spara.'), fel: true });
    } finally {
      setArbetar(false);
    }
  }

  async function växlaAktiv(a: Användare) {
    if (a.aktiv && !window.confirm(`${visningsnamn(a)} kommer inte längre kunna logga in. Fortsätta?`)) return;
    setArbetar(true);
    try {
      const res = await api.post(`/auth/anvandare/${a.id}/aktiv`, { aktiv: !a.aktiv });
      const nyttDatum: string | null = res.data?.giltig_till || null;
      setRadBesked({
        text: a.aktiv ? `${visningsnamn(a)} är inaktiverad.`
          : nyttDatum ? `${visningsnamn(a)} är aktiverad igen. ${giltigText(nyttDatum).text}.`
          : `${visningsnamn(a)} är aktiverad igen.`,
        fel: false,
      });
      if (nyttDatum && utkast && utkast.id === a.id) setUtkast({ ...utkast, giltig_till: nyttDatum });
      await ladda();
    } catch (err) {
      setRadBesked({ text: felText(err, 'Kunde inte ändra.'), fel: true });
    } finally {
      setArbetar(false);
    }
  }

  async function taBortGäst(a: Användare) {
    const vem = visningsnamn(a);
    if (!window.confirm(`Ta bort ${vem}? Inloggningen och uppgifterna om gästen raderas och går inte att få tillbaka.`)) return;
    setArbetar(true);
    try {
      await api.delete(`/auth/anvandare/${a.id}`);
      setÖppen(null);
      setUtkast(null);
      setRadBesked(null);
      setMeddelande(`${vem} är borttagen.`);
      await ladda();
    } catch (err) {
      setRadBesked({ text: felText(err, 'Kunde inte ta bort gästen.'), fel: true });
    } finally {
      setArbetar(false);
    }
  }

  async function skickaLösenordslänk(a: Användare) {
    setArbetar(true);
    try {
      const res = await api.post(`/auth/anvandare/${a.id}/losenordslank`);
      setRadBesked({ text: `Länk skickad till ${res.data.email}.`, fel: false });
    } catch (err) {
      setRadBesked({ text: felText(err, 'Kunde inte skicka länken.'), fel: true });
    } finally {
      setArbetar(false);
    }
  }

  if (laddar) return <p style={{ color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>Laddar...</p>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '26px', color: TEMA.ink, margin: 0 }}>Inställningar</h1>
        {ärAdmin && <button onClick={() => setVisaForm(!visaForm)} style={btnPrimärStyle}>{demo ? '+ Ny gäst' : '+ Ny användare'}</button>}
      </div>
      <InstallningarFlikar />

      {fel && <p style={{ color: '#D9695F', fontSize: '13px', marginBottom: '18px', fontFamily: "'Inter', sans-serif" }}>{fel}</p>}
      {meddelande && <p style={{ color: TEMA.positive, fontSize: '13px', marginBottom: '18px', fontFamily: "'Inter', sans-serif" }}>{meddelande}</p>}

      {visaForm && (
        <div style={{ background: TEMA.bgPanel, border: `1px solid ${TEMA.border}`, borderRadius: '8px', padding: '22px 24px', marginBottom: '16px' }}>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: TEMA.ink, margin: demo ? '0 0 8px' : '0 0 18px' }}>{demo ? 'Bjud in en gäst' : 'Skapa ny användare'}</h2>
          {demo && (
            <p style={{ margin: '0 0 18px', fontSize: '13px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif", lineHeight: 1.5 }}>
              Gästen loggar in med sin egen e-post men ser Elsas vy. Inloggningen stängs av sig själv dagen efter slutdatumet.
            </p>
          )}
          <form onSubmit={skapaAnvändare}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: '14px', marginBottom: '14px' }}>
              <div>
                <label style={labelStyle}>Namn</label>
                <input style={inputStyle} value={nyNamn} onChange={e => setNyNamn(e.target.value)} placeholder="Förnamn" required />
              </div>
              <div>
                <label style={labelStyle}>E-post</label>
                <input type="email" style={inputStyle} value={nyEmail} onChange={e => setNyEmail(e.target.value)} placeholder="namn@foretag.se" required />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: '14px', marginBottom: '18px' }}>
              <div>
                <label style={labelStyle}>Lösenord</label>
                <input type="password" style={inputStyle} value={nyttLösenord} onChange={e => setNyttLösenord(e.target.value)} placeholder="Minst 6 tecken" required />
              </div>
              <div>
                {demo ? (
                  <>
                    <label style={labelStyle} htmlFor="ny-giltig-till">Gäller till och med</label>
                    <input id="ny-giltig-till" type="date" style={inputStyle} value={nyGiltigTill} min={datumOm(0)} onChange={e => setNyGiltigTill(e.target.value)} required />
                  </>
                ) : (
                  <>
                    <label style={labelStyle}>Roll</label>
                    <select style={inputStyle} value={nyRoll} onChange={e => setNyRoll(e.target.value)}>
                      <option value="säljare">Säljare</option>
                      <option value="admin">Admin</option>
                    </select>
                  </>
                )}
              </div>
            </div>
            <button type="submit" disabled={skapar} style={{ ...btnPrimärStyle, opacity: skapar ? 0.6 : 1 }}>
              {skapar ? 'Skapar...' : demo ? 'Bjud in gäst' : 'Skapa användare'}
            </button>
          </form>
        </div>
      )}

      {inbjudan && (
        <div style={{ background: TEMA.bgPanel, border: `1px solid ${TEMA.accent}`, borderRadius: '8px', padding: '22px 24px', marginBottom: '16px' }}>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: TEMA.ink, margin: '0 0 8px' }}>Skicka det här till gästen</h2>
          <p style={{ margin: '0 0 14px', fontSize: '13px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif", lineHeight: 1.5 }}>
            Kopiera texten och skicka den från din egen mejl. Lösenordet visas bara här och nu.
          </p>
          <pre style={{
            margin: '0 0 16px', padding: '14px 16px', background: TEMA.bg, border: `1px solid ${TEMA.border}`,
            borderRadius: '2px', color: TEMA.ink, fontFamily: "'Inter', sans-serif", fontSize: '13.5px',
            lineHeight: 1.55, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
          }}>{inbjudan}</pre>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              style={btnPrimärStyle}
              onClick={() => {
                navigator.clipboard.writeText(inbjudan).then(() => setKopierad(true)).catch(() => setKopierad(false));
              }}
            >
              {kopierad ? 'Kopierad' : 'Kopiera texten'}
            </button>
            <button type="button" style={btnGhostStyle} onClick={() => setInbjudan(null)}>Stäng</button>
          </div>
        </div>
      )}

      <div style={{ background: TEMA.bgPanel, border: `1px solid ${TEMA.border}`, borderRadius: '8px', overflow: 'hidden' }}>
        {lista.map((a, index) => {
          const ärJag = a.id === jagId;
          const ärÖppen = öppen === a.id && utkast;
          const giltig = a.gast ? giltigText(a.giltig_till) : null;
          return (
            <div key={a.id} style={{ borderBottom: index < lista.length - 1 ? `1px solid ${TEMA.border}` : 'none' }}>
              <div
                onClick={() => { if (ärAdmin || ärJag) växlaÖppen(a); }}
                style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', cursor: ärAdmin || ärJag ? 'pointer' : 'default', opacity: a.aktiv ? 1 : 0.55 }}
              >
                <Avatar id={a.id} namn={visningsnamn(a)} bildUrl={bildUrl(a.id)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '15px', color: TEMA.ink }}>
                    {visningsnamn(a)}{ärJag && <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: TEMA.inkFaint }}> (du)</span>}
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>
                    {a.gast ? 'Gäst' : (ROLLNAMN[a.roll] || a.roll)} · <span style={{ color: TEMA.inkFaint }}>{senastText(a.senast_inloggad)}</span>
                  </p>
                  {a.gast && giltig && (
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif", overflowWrap: 'anywhere' }}>
                      {a.gast_namn ? <>{a.email} · </> : null}<span style={{ color: giltig.utgången ? '#D9695F' : TEMA.inkSoft }}>{giltig.text}</span>
                    </p>
                  )}
                </div>
                <span style={{
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', letterSpacing: '0.04em',
                  padding: '3px 10px', borderRadius: '2px',
                  border: `1px solid ${a.aktiv ? TEMA.positive : TEMA.border}`,
                  color: a.aktiv ? TEMA.positive : TEMA.inkSoft,
                }}>
                  {a.aktiv ? 'Aktiv' : 'Inaktiv'}
                </span>
                {(ärAdmin || ärJag) ? <span style={{ marginLeft: '14px', color: TEMA.inkFaint, fontSize: '11px' }}>{öppen === a.id ? '▲' : '▼'}</span> : <span style={{ marginLeft: '14px', width: '11px' }} />}
              </div>

              {ärÖppen && utkast && (
                <div style={{ padding: ärMobil ? '4px 16px 18px' : '4px 20px 20px 70px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: '14px', marginBottom: '14px' }}>
                    <div>
                      <label style={labelStyle}>Namn</label>
                      {a.gast && ärAdmin ? (
                        <input style={inputStyle} value={utkast.gast_namn || ''} placeholder="Gästens namn" onChange={e => setUtkast({ ...utkast, gast_namn: e.target.value })} />
                      ) : (
                        <input style={inputStyle} value={utkast.namn || ''} onChange={e => setUtkast({ ...utkast, namn: e.target.value })} />
                      )}
                    </div>
                    <div>
                      <label style={labelStyle}>E-post</label>
                      <input type="email" style={{ ...inputStyle, opacity: ärAdmin ? 1 : 0.5 }} disabled={!ärAdmin} title={ärAdmin ? undefined : 'E-post ändras av en admin'} value={utkast.email || ''} onChange={e => setUtkast({ ...utkast, email: e.target.value })} />
                    </div>
                    {!a.gast && <div>
                      <label style={labelStyle}>Roll</label>
                      <select
                        style={{ ...inputStyle, opacity: ärJag ? 0.5 : 1 }}
                        value={utkast.roll}
                        disabled={ärJag}
                        title={ärJag ? 'Du kan inte ändra din egen roll' : undefined}
                        onChange={e => setUtkast({ ...utkast, roll: e.target.value })}
                      >
                        <option value="säljare">Säljare</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>}
                    {a.gast && ärAdmin && (
                      <div>
                        <label style={labelStyle} htmlFor={`giltig-${a.id}`}>Gäller till och med</label>
                        <input id={`giltig-${a.id}`} type="date" style={inputStyle} value={utkast.giltig_till || ''} onChange={e => setUtkast({ ...utkast, giltig_till: e.target.value })} />
                      </div>
                    )}
                  </div>

                  {ärAdmin && (
                    <div style={{ marginBottom: '14px' }}>
                      <span style={labelStyle}>Moduler</span>
                      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', fontFamily: "'Inter', sans-serif", fontSize: '13.5px', color: TEMA.ink }}>
                        {MODULVAL.map(([id, text]) => {
                          const valda = utkast.moduler || [];
                          return (
                            <label key={id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={valda.includes(id)}
                                onChange={() => setUtkast({
                                  ...utkast,
                                  moduler: MODULVAL.map(([m]) => m).filter(m => (m === id ? !valda.includes(id) : valda.includes(m))),
                                })}
                                style={{ width: '16px', height: '16px', accentColor: TEMA.accent }}
                              />
                              {text}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '24px', marginBottom: '18px', fontFamily: "'Inter', sans-serif", fontSize: '13.5px', color: TEMA.ink }}>
                    {([
                      ['daglig_sammanfattning', 'Morgonmejl kl. 07'],
                      ['eftermiddag_paminnelse', 'Påminnelse kl. 15'],
                    ] as const).map(([fält, text]) => (
                      <label key={fält} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={utkast[fält]}
                          onChange={() => setUtkast({ ...utkast, [fält]: !utkast[fält] })}
                          style={{ width: '16px', height: '16px', accentColor: TEMA.accent }}
                        />
                        {text}
                      </label>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button onClick={() => spara(a)} disabled={arbetar} style={{ ...btnPrimärStyle, opacity: arbetar ? 0.6 : 1 }}>
                      Spara ändringar
                    </button>
                    {ärAdmin && !demo && (
                      <button onClick={() => skickaLösenordslänk(a)} disabled={arbetar} style={btnGhostStyle}>
                        Skicka länk för nytt lösenord
                      </button>
                    )}
                    {ärAdmin && <button
                      onClick={() => växlaAktiv(a)}
                      disabled={arbetar || ärJag}
                      title={ärJag ? 'Du kan inte inaktivera dig själv' : undefined}
                      style={{ ...btnGhostStyle, marginLeft: 'auto', opacity: ärJag ? 0.4 : 1, cursor: ärJag ? 'default' : 'pointer' }}
                    >
                      {a.aktiv ? 'Inaktivera' : 'Aktivera'}
                    </button>}
                    {ärAdmin && demo && a.gast && !ärJag && (
                      <button
                        onClick={() => taBortGäst(a)}
                        disabled={arbetar}
                        style={{ ...btnGhostStyle, color: '#D9695F', borderColor: 'rgba(217,105,95,0.45)' }}
                      >
                        Ta bort gäst
                      </button>
                    )}
                  </div>

                  {radBesked && (
                    <p style={{ margin: '12px 0 0', fontSize: '13px', fontFamily: "'Inter', sans-serif", color: radBesked.fel ? '#D9695F' : TEMA.positive }}>
                      {radBesked.text}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
